import { faMinus } from "@fortawesome/pro-regular-svg-icons";
import { useReducedMotion } from "motion/react";

import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import type { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { FileViewerWindowModeContext } from "src/files/fileViewerWindowMode";
import { UnifiedCopilotChat } from "src/unifiedCopilot/chat/UnifiedCopilotChat";
import { useUnifiedCopilotDisplay } from "src/unifiedCopilot/UnifiedCopilotDisplayProvider";
import {
  UnifiedCopilotNewConversationButton,
  UnifiedCopilotSessionDebugActions,
  UnifiedCopilotSwitchModeButton,
} from "src/unifiedCopilot/UnifiedCopilotHeader";
import { UnifiedCopilotSessionHistoryDropdown } from "src/unifiedCopilot/UnifiedCopilotSessionHistoryDropdown";
import { useCopilotWindowGeometry } from "src/unifiedCopilot/useCopilotWindowGeometry";

export const UnifiedCopilotWindow: React.FC<FloatingWindowProps> = ({
  computePosition,
  id,
}) => {
  const { position, windowRef } = useFloatingWindowPosition(computePosition);
  const { surface, hide } = useUnifiedCopilotDisplay();
  const { restoredGeometry, persistGeometry } = useCopilotWindowGeometry();
  const reduceMotion = useReducedMotion();

  // Keep the window mounted while hiding so AnimatePresence can finish its
  // exit. Mode changes still transfer the chat immediately.
  if (surface === "sidebar" || surface === "page") return null;

  return (
    <FloatingWindowInternal
      ref={windowRef}
      clipOverflow={false}
      closeIcon={faMinus}
      closeTooltip="Hide window"
      dataLoc="unified-copilot-window"
      height={restoredGeometry?.height ?? "h-150"}
      id={id}
      isPinned={false}
      minHeight="h-96"
      minWidth="w-96"
      motionVariants={{
        visible: {
          opacity: 1,
          scale: 1,
          y: 0,
          originX: 1,
          originY: 1,
          transition: reduceMotion
            ? { duration: 0 }
            : {
                type: "spring",
                stiffness: 380,
                damping: 30,
                mass: 0.8,
                opacity: { duration: 0.18 },
              },
        },
        hidden: {
          opacity: 0,
          scale: reduceMotion ? 1 : 0.94,
          y: reduceMotion ? 0 : 18,
          originX: 1,
          originY: 1,
          transition: {
            duration: reduceMotion ? 0 : 0.16,
            ease: [0.4, 0, 1, 1],
          },
        },
      }}
      style={{
        inset: "0px auto auto 0px",
        transform: restoredGeometry
          ? `translate3d(${restoredGeometry.x}px, ${restoredGeometry.y}px, 0)`
          : position,
      }}
      title={<UnifiedCopilotSessionHistoryDropdown />}
      titleRight={
        <>
          <UnifiedCopilotSessionDebugActions tooltipPlacement="top" />
          <UnifiedCopilotNewConversationButton tooltipPlacement="top" />
          <UnifiedCopilotSwitchModeButton
            mode="sidebar"
            tooltipPlacement="top"
          />
        </>
      }
      width={restoredGeometry?.width ?? "w-134"}
      draggable
      maximizable
      resizeable
      onClose={hide}
      onGeometryChange={persistGeometry}
    >
      {/* The trace is re-derived on every poll, so the file viewer opens as a
          window of the store rather than one owned by the card that opened it. */}
      <FileViewerWindowModeContext value="shared">
        <UnifiedCopilotChat />
      </FileViewerWindowModeContext>
    </FloatingWindowInternal>
  );
};
